/*************************************************************************
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying
 * it. If you have received this file from a source other than Adobe,
 * then your use, modification, or distribution of it requires the prior
 * written permission of Adobe.
 **************************************************************************/

/**
 * APIs to let solutions make network requests for resources, execute CRUD operations, etc.
 *
 * The default export is an object of type [NetworkApi](../interfaces/_network_.networkapi.md)
 * @packageDocumentation
 */

import {getImpl} from './src/Global';

interface FetchOptions {
  /**
   * A boolean value indicating whether or not to add an authorization header to the request.
   */
  auth?: 'Header' | 'Query';
}

/**
 * @ignore
 */
export interface Configuration {
  /**
   * appId from solution route config
   */
  appId: string;
  /**
   * Unified Shell's environment- test/dev/qa/stage/prod.
   */
  environment: string;
  /**
   * Get Authentication token
   */
  getTokenInfo?: () => Promise<{token: string}>;
  /**
   * IMS Client ID
   */
  imsClientId: string;
  /**
   * IMS Token - Authentication Token
   */
  imsToken: string;
  /**
   * metricsAppId from solution route config
   */
  metricsAppId?: string;
  /**
   * tenantAppId from solution route config
   */
  tenantAppId?: string;
}

/**
 * GQL Query containing graphql query and variables.
 */
export interface GraphQLQuery {
  /**
   * GQL Query.
   */
  query: string;
  /**
   * Query specific variables- key value pairs.
   */
  variables?: Record<string, any>;
}

/**
 * Query request interface.
 */
export interface QueryRequest {
  /**
   * Data containing single or multiple GQL queries.
   */
  data: GraphQLQuery | Array<GraphQLQuery>;
}

/**
 * Defines the object containing any custom settings that you want to apply to the request. You can
 * also additionally specify the 'auth' parameter to automatically set the Authorization header.
 */
export type FetchInit = RequestInit & FetchOptions;

export interface NetworkApi {
  /**
   * @ignore
   */
  configure(config: Configuration): void;
  /**
   * Provides an interface for fetching resources powered by the global 'fetch' API.
   * @param input The resource that you wish to fetch. It can either be the URL of the resource
   * you want to fetch or a Request object.
   * @param init An object containing any custom settings that you want to apply to the request.
   * @returns The promise for the response to the fetch operation.
   */
  fetch(input: RequestInfo, init?: FetchInit): Promise<Response>;
  /**
   * Provides an interface for querying resources via GraphqQL.
   * In order to consume query, please make sure the respective query resolver is
   * available at {@link https://git.corp.adobe.com/exc/graphql-service GraphQL Service}.
   * For more info on writing a new resolver,
   * please use:  {@link https://git.corp.adobe.com/exc/graphql-service/blob/master/CONTRIBUTING.md#contributing-a-new-module GraphQL New Module}.
   * @param input Query request containing desired GQL Query.
   * @returns The promise for the response to the query operation.
   */
  query(input: QueryRequest): Promise<Response>;
}

/**
 * Provides an interface for fetching resources powered by the global 'fetch' API.
 * @param input The resource that you wish to fetch. It can either be the URL of the resource you
 * want to fetch or a Request object.
 * @param init An object containing any custom settings that you want to apply to the request.
 * @returns The promise for the response to the fetch operation.
 */
export function fetch(input: RequestInfo, init?: FetchInit): Promise<Response> {
  return getImpl('network').fetch(input, init);
}

/**
 * Provides an interface for querying resources via GraphqQL.
 * In order to consume query, please make sure the respective query resolver is
 * available at {@link https://git.corp.adobe.com/exc/graphql-service GraphQL Service}.
 * For more info on writing a new resolver,
 * please use:  {@link https://git.corp.adobe.com/exc/graphql-service/blob/master/CONTRIBUTING.md#contributing-a-new-module GraphQL New Module}.
 * @param input Query request containing desired GQL Query.
 * @returns The promise for the response to the query operation.
 */
export function query(input: QueryRequest): Promise<Response> {
  return getImpl('network').query(input);
}

/**
 * @ignore
 */
export class Internal {
  public static configure(config: Configuration): void {
    return getImpl('network').configure(config);
  }
}
